import { Injectable, NgZone } from '@angular/core';
import { AppConfig, DruckerKonfiguration } from './helper/app.config';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { Logger, LogLevel } from './helper/app-error-logger';
import { Platform } from '@ionic/angular';
import { MyApp } from './helper/app';
import { Printer, PrintOptions } from '@awesome-cordova-plugins/printer/ngx';

import { delay } from 'rxjs/operators';

import { Utils } from './helper/utils';
import { ZbtPrinter } from './model/zebra-printer';
import { SystemService } from './system.service';
import { UiHelper } from './helper/ui-helper';
import { BluetoothDevice, DruckerDevice, GeraetStatusTyp } from './model/model';
import { WaageService } from './waage.service';
import { BluetoothSerial } from '@awesome-cordova-plugins/bluetooth-serial/ngx';

import ReceiptPrinterEncoder from '@point-of-sale/receipt-printer-encoder';

declare var cordova: any;

const log = new Logger("DruckerService", LogLevel.INFO);

@Injectable({
    providedIn: 'root'
})
export class DruckerService {

    druckerStatus = new BehaviorSubject<GeraetStatusTyp>(GeraetStatusTyp.Unbekannt);

    /**
     * Liste aktuell verbundener Drucker
     */
    verbundeneDrucker = new BehaviorSubject<DruckerKonfiguration[]>([]);

    sucheDruckerPromise: Promise<DruckerDevice[]> = null;
    zebraStatusPromise: Promise<boolean> = null;

    druckenPromise: Promise<boolean> = null;

    letzteSuche = 0;

    updateIntervalId: any = null;

    sucheDruckerLaeuft = false;
    zebraStatusLaeuft = false;
    wirdGedruckt = false;

    connectSubscription: Subscription;

    constructor(
        private systemService: SystemService,
        private platform: Platform,
        private waageService: WaageService,
        private bluetoothSerial: BluetoothSerial,
        private printer: Printer,
        private ngZone: NgZone,) {

    }

    async init() {
        log?.debug('init');

        try {
            await delay(10 * 1000);

            this.systemService.istImVordergrund.subscribe(() => {
                this.updateTimer();
            });

            MyApp.current.configRefreshed.subscribe(() => this.updateTimer());
        } catch (err) {
            log.error('initialize', err);
        }
    }

    updateTimer() {
        if (this.updateIntervalId) {
            clearInterval(this.updateIntervalId);
            this.updateIntervalId = null;
        }

        if (AppConfig.current.einstellungen.DruckerVerfuegbar !== 'ja') {
            return;
        }

        const imVordergrund = this.systemService.istImVordergrund.getValue();

        const interval = imVordergrund ? 60 : 300;

        this.updateIntervalId = setInterval(() => {
            this.update();
        }, interval * 1000);

        if (imVordergrund) {
            this.update();
        }
    }

    update() {
        if (AppConfig.current.einstellungen.DruckerVerfuegbar !== 'ja') {
            return;
        }

        log?.debug('update');

        try {
            let timeout = 30 * 1000;

            if (this.druckerStatus.getValue() === GeraetStatusTyp.Verbunden) {
                // Wenn der Drucker verbunden ist, dann nur alle 5 Minuten prüfen.
                // Ansonsten stört das beim Drucken
                timeout = 5 * 60 * 1000;
            }

            if (this.letzteSuche < Date.now() - timeout) {
                this.updateDruckerVerbindung();
            }
        } catch (err) {
            log.error('update updateDruckerVerbindung', err);
        }
    }

    isFunktionAktiv() {
        return this.sucheDruckerLaeuft || this.zebraStatusLaeuft || this.wirdGedruckt;
    }

    async updateDruckerVerbindung() {
        log?.debug('updateDruckerVerbindung');

        if (!this.systemService.istImVordergrund.getValue()) {
            log?.debug('updateDruckerVerbindung: Anwendung nicht im Vordergrund. Abbruch');
            return;
        }

        await MyApp.ready();

        switch (AppConfig.current.einstellungen.DruckerTyp) {
            case 'Druckdienst':
                await this.updateDruckerVerbindungDruckdienst();
                break;

            case 'zebra':
                await this.updateDruckerVerbindungZebra();
                break;
        }
    }

    async updateDruckerVerbindungDruckdienst(): Promise<void> {
        if (!MyApp.isCordovaAvailable()) {
            log?.debug('Cordova nicht verfügbar');
            return;
        }

        // TODO: Geht mit Capacitor nocht nicht.
        // try {
        //     const available = await this.printer.isAvailable();

        //     if (available) {
        //         this.druckerStatus.next(GeraetStatusTyp.Unbekannt);
        //     } else {
        //         this.druckerStatus.next(GeraetStatusTyp.NichtVerbunden);
        //     }
        // } catch (err) {
        //     log.error('updateDruckerVerbindungHP: ' + Utils.getErrorMessage(err), err);
        //     this.druckerStatus.next(GeraetStatusTyp.Fehler);
        // }
    }

    async updateDruckerVerbindungZebra(): Promise<void> {
        const appConfig = AppConfig.current;

        if (!appConfig.gekoppelteDrucker) {
            appConfig.gekoppelteDrucker = [];
        }

        if (!appConfig.gekoppelteDrucker.length) {
            log?.debug('updateDruckerVerbindung keine Drucker gekoppelt');
            this.druckerStatus.next(GeraetStatusTyp.NichtVerbunden);
            return;
        }

        if (!MyApp.isCordovaAvailable()) {
            log?.debug('Cordova nicht verfügbar');
            return;
        }

        const zbtprinter = (cordova.plugins as any).zbtprinter as ZbtPrinter;

        if (!zbtprinter) {
            log.warn('ZbtPrinter Plugin nicht installiert');
            this.druckerStatus.next(GeraetStatusTyp.Fehler);
            return;
        }

        if (this.sucheDruckerLaeuft) {
            log?.debug('updateDruckerVerbindungZebra: sucheDruckerLaeuft, abbruch');
            return;
        }

        if (this.zebraStatusLaeuft) {
            log?.debug('updateDruckerVerbindungZebra: zebraStatusLaeuft, abbruch');
            return;
        }

        if (this.isFunktionAktiv()) {
            log?.debug('updateDruckerVerbindungZebra: isFunktionAktiv() = TRUE, abbruch');
            return;
        }

        this.letzteSuche = Date.now();

        for (const drucker of appConfig.gekoppelteDrucker) {
            if (drucker.macAdresse && drucker.aktiv && drucker.typ === 'ZEBRA') {
                log?.debug('Prüfe Verbindung zu Drucker: ' + drucker.macAdresse, + ', ' + drucker.name);

                // Prüfe nur explizit die Verbindung zu diesem Drucker
                this.zebraStatusLaeuft = true;

                this.zebraStatusPromise = new Promise(async (resolve, reject) => {
                    let istResolved = false;

                    zbtprinter.getStatus(drucker.macAdresse,
                        success => {
                            log?.debug('updateDruckerVerbindungZebra: ' + success);
                            this.letzteSuche = Date.now();
                            this.zebraStatusLaeuft = false;
                            this.druckerStatus.next(GeraetStatusTyp.Verbunden);

                            if (!istResolved) {
                                istResolved = true;

                                resolve(false);

                                this.zebraStatusLaeuft = false;
                                this.zebraStatusPromise = null;
                            }
                        },
                        fail => {
                            log.warn('updateDruckerVerbindungZebra failed: ' + Utils.getErrorMessage(fail), fail);
                            this.letzteSuche = Date.now();
                            this.zebraStatusLaeuft = false;
                            this.druckerStatus.next(GeraetStatusTyp.Fehler);

                            if (!istResolved) {
                                istResolved = true;

                                resolve(false);

                                this.zebraStatusLaeuft = false;
                                this.zebraStatusPromise = null;
                            }
                        });

                    setTimeout(() => {
                        log?.debug('resolve timeout');

                        if (!istResolved) {
                            log?.debug('updateDruckerVerbindungZebra: resolve timeout: noch nicht resolved');
                            this.letzteSuche = Date.now();
                            this.druckerStatus.next(GeraetStatusTyp.Unbekannt);

                            istResolved = true;

                            resolve(false);

                            this.zebraStatusLaeuft = false;
                            this.zebraStatusPromise = null;
                        }
                    }, MyApp.current.druckerSuchenTimeout);
                });

                return;
            }
        }

        // this.sucheDrucker(false);
    }

    /**
     * Sucht erreichbare Drucker und gibt eine Liste an MAC-Adressen zurück
     */
    async sucheDrucker(mitFeedback: boolean): Promise<DruckerDevice[]> {
        log?.debug('sucheDrucker');

        this.letzteSuche = Date.now();

        if (mitFeedback && !MyApp.isCordovaAvailable()) {
            await UiHelper.showAlert('Drucker suchen in Simulation nicht verfügbar', 'Simulation', true);
        }

        if (this.sucheDruckerPromise) {
            log?.debug('sucheDruckerPromise schon vorhanden.');

            if (mitFeedback) {
                // Nur warten, aber nicht das Ergebnis zurückliefern
                await this.sucheDruckerPromise;
            } else {
                return this.sucheDruckerPromise;
            }
        }

        if (this.druckenPromise) {
            // Warte bis Druck abgeschlossen ist
            await this.druckenPromise;
        }

        switch (AppConfig.current.einstellungen.DruckerTyp) {
            case 'Druckdienst':
                return await this.sucheStandardDrucker(mitFeedback);

            case 'zebra':
                return await this.sucheZebraDrucker();

            case 'escpos':
                return await this.sucheEscPosDrucker();
        }

        return [];
    }

    async verbindeByMacAdresse(macAdresse: string): Promise<boolean> {
        log?.info('verbindeByMacAdresse: ' + macAdresse);

        this.letzteSuche = Date.now();

        if (!macAdresse || macAdresse.length !== 12) {
            await UiHelper.showAlert('Ungültige MAC-Adresse: ' + macAdresse, 'Fehler', true);
            return;
        }

        if (!MyApp.isCordovaAvailable()) {
            await UiHelper.showAlert('Drucker verbinden in Simulation nicht verfügbar', 'Simulation', true);
            return;
        }

        try {
            await UiHelper.showLoading('Durcker wird verbunden');

            // Status 
            const status = await this.getZebraStatus(macAdresse);

            log?.info("STATUS: " + status);

            if (status.startsWith('Could not connect to device')) {
                await UiHelper.showAlert(status, 'Fehler');
                return false;
            }

            await Utils.delay(500);

            // Name ermitteln
            let name = await this.getZebraPrinterName(macAdresse);

            if (!name) {
                name = macAdresse;
            }

            await Utils.delay(500);

            const appConfig = AppConfig.current;

            if (!appConfig.gekoppelteDrucker) {
                appConfig.gekoppelteDrucker = [];
            }

            // Zuvor gekoppelten Drucker inaktiv setzen
            for (const drucker of appConfig.gekoppelteDrucker) {
                drucker.aktiv = false;
            }

            let konfiguration = this.getDruckerKonfigurationByMacAdresse(macAdresse);

            if (!konfiguration) {
                konfiguration = {
                    typ: 'ZEBRA',
                    macAdresse: macAdresse,
                    aktiv: false,
                    istVorkonfiguriert: false,
                    name: name,
                    zuletztVerwendet: '',
                };

                appConfig.gekoppelteDrucker.push(konfiguration);
            }

            konfiguration.aktiv = true;
            konfiguration.zuletztVerwendet = new Date().toISOString();

            this.systemService.speichereAppConfig(appConfig);

            this.druckerStatus.next(GeraetStatusTyp.Verbunden);

            await UiHelper.hideLoading();

            return true;
        } catch (err) {
            log.error('verbindeByMacAdresse ' + macAdresse + ': ' + Utils.getErrorMessage(err), err);

            await UiHelper.hideLoading();

            UiHelper.showErrorOhneSentry(err);
            return false;
        }
    }

    getZebraStatus(macAdresse: string): Promise<string> {
        return new Promise((resolve, reject) => {
            try {
                const zbtprinter = (cordova.plugins as any).zbtprinter as ZbtPrinter;
                zbtprinter.getStatus(macAdresse,
                    status => {
                        resolve(status);
                    }, e => {
                        log.warn('getZebraPrinterName getPrinterName: ' + Utils.getErrorMessage(e));
                        resolve(Utils.getErrorMessage(e));
                    });
            } catch (err) {
                log.warn('getZebraPrinterName: ' + Utils.getErrorMessage(macAdresse));
            }
        });
    }

    getZebraPrinterName(macAdresse: string): Promise<string> {
        return new Promise((resolve, reject) => {
            try {
                const zbtprinter = (cordova.plugins as any).zbtprinter as ZbtPrinter;
                zbtprinter.getPrinterName(macAdresse,
                    printerName => {
                        resolve(printerName);
                    }, e => {
                        log.warn('getZebraPrinterName getPrinterName: ' + Utils.getErrorMessage(e));
                        resolve('');
                    });
            } catch (err) {
                log.warn('getZebraPrinterName: ' + Utils.getErrorMessage(macAdresse));
            }
        });
    }

    async sucheStandardDrucker(mitFeedback: boolean): Promise<DruckerDevice[]> {
        try {
            if (!MyApp.isCordovaAvailable()) {
                return [{ name: 'Druckdienst', address: 'Druckdienst' }];
            }

            const available = await this.printer.isAvailable();

            if (available) {
                if (mitFeedback) {
                    const pickResult = await this.printer.pick();
                    log?.debug('pickResult: ' + pickResult, pickResult);
                }

                return [{ name: 'Druckdienst', address: 'Druckdienst' }];
            } else {
                return [];
            }
        } catch (err) {
            log.warn('sucheStandardDrucker: ' + Utils.getErrorMessage(err), err);
            return [];
        }
    }

    async sucheZebraDrucker(): Promise<DruckerDevice[]> {
        this.sucheDruckerLaeuft = true;

        const appConfig = AppConfig.current;

        if (!appConfig.gekoppelteDrucker) {
            appConfig.gekoppelteDrucker = [];
        }

        if (!MyApp.isCordovaAvailable()) {
            return [{ name: 'ZPL-Simulator', address: 'ZPL-Simulator' }];
        }

        // Sortiere absteigend nach letzter Verwendung
        appConfig.gekoppelteDrucker = appConfig.gekoppelteDrucker.sort((a, b) => a.zuletztVerwendet < b.zuletztVerwendet ? 1 : -1);

        const gefundeneDrucker: DruckerKonfiguration[] = [];

        try {
            const zbtprinter = (cordova.plugins as any).zbtprinter as ZbtPrinter;

            if (!zbtprinter) {
                log.warn('ZbtPrinter Plugin nicht installiert');
                this.druckerStatus.next(GeraetStatusTyp.Fehler);
                return [];
            }

            if (this.druckerStatus.getValue() != GeraetStatusTyp.Verbunden) {
                this.druckerStatus.next(GeraetStatusTyp.Suche);
            }

            this.sucheDruckerPromise = new Promise(async (resolve, reject) => {
                const macAdressen: string[] = [];
                let istResolved = false;

                zbtprinter.discoverPrinters(
                    async MACAddress => {
                        log?.debug('Drucker gefunden: ' + MACAddress);
                        this.letzteSuche = Date.now();

                        macAdressen.push(MACAddress.trim());

                        this.ngZone.run(async () => {
                            const drucker = this.getDruckerKonfigurationByMacAdresse(MACAddress);

                            if (drucker && drucker.aktiv) {
                                gefundeneDrucker.push(drucker);
                                this.druckerStatus.next(GeraetStatusTyp.Verbunden);
                            }
                        });
                    }, fail => {
                        this.ngZone.run(() => {
                            log.warn('ZbtPrinter discoverPrinters: ' + Utils.getErrorMessage(fail), fail);
                            this.druckerStatus.next(GeraetStatusTyp.Fehler);
                            this.letzteSuche = Date.now();

                            if (!istResolved) {
                                log?.debug('resolve: ' + macAdressen.join(', '));

                                istResolved = true;
                                this.verbundeneDrucker.next(gefundeneDrucker);

                                var druckerDevices: DruckerDevice[] = macAdressen.map(mac => ({ name: mac, address: mac }));

                                resolve(druckerDevices);

                                this.sucheDruckerLaeuft = false;
                                this.sucheDruckerPromise = null;
                            }
                        });
                    }
                );

                setTimeout(() => {
                    log?.debug('resolve timeout');

                    if (!istResolved) {
                        log?.debug('resolve timeout: noch nicht resolved: ' + macAdressen.join(', '));
                        this.letzteSuche = Date.now();

                        istResolved = true;
                        this.verbundeneDrucker.next(gefundeneDrucker);

                        var druckerDevices: DruckerDevice[] = macAdressen.map(mac => ({ name: mac, address: mac }));

                        resolve(druckerDevices);

                        this.sucheDruckerLaeuft = false;
                        this.sucheDruckerPromise = null;
                    }
                }, MyApp.current.druckerSuchenTimeout);
            });

            return this.sucheDruckerPromise;
        } catch (err) {
            log.error('sucheDrucker', err);
            return [];
        }
    }

    async sucheEscPosDrucker(): Promise<DruckerDevice[]> {
        log?.info('sucheEscPosDrucker');

        this.sucheDruckerLaeuft = true;

        const appConfig = AppConfig.current;

        if (!appConfig.gekoppelteDrucker) {
            appConfig.gekoppelteDrucker = [];
        }

        if (!MyApp.isCordovaAvailable()) {
            return [{ name: 'ESC-POS-Simulator', address: 'ESC-POS-Simulator' }];
        }

        // Sortiere absteigend nach letzter Verwendung
        appConfig.gekoppelteDrucker = appConfig.gekoppelteDrucker.sort((a, b) => a.zuletztVerwendet < b.zuletztVerwendet ? 1 : -1);

        try {
            const bluetoothGeraete = await this.waageService.sucheBluetoothGeraete();

            this.sucheDruckerLaeuft = false;

            if (!bluetoothGeraete || bluetoothGeraete.length == 0) {
                UiHelper.showAlert('Kein gekopptelten Bluetooth-Geräte gefunden');
                return;
            }

            for (const d of bluetoothGeraete) {
                if (!d.name) {
                    d.name = d.address;
                }
            }

            const neueGeraete: BluetoothDevice[] = [];

            for (const device of bluetoothGeraete) {
                const config = this.getDruckerKonfigurationByMacAdresse(device.address);

                if (!config) {
                    // Dieser Drucker ist noch nicht gekoppelt. Also als neues Gerät anzeigen.
                    neueGeraete.push(device);
                }
            }

            this.update();

            const druckerDevices: DruckerDevice[] = neueGeraete.map(d => ({ name: d.name, address: d.address }));

            return druckerDevices;
        } catch (err) {
            log.error('sucheDrucker', err);
            return [];
        }
    }

    getDruckerKonfigurationByMacAdresse(macAdresse: string): DruckerKonfiguration {
        const appConfig = AppConfig.current;

        return appConfig.gekoppelteDrucker.find(p => p.macAdresse && p.macAdresse.toLowerCase() == macAdresse.toLowerCase());
    }

    getAktiveDrucker(): DruckerKonfiguration[] {
        return AppConfig.current.gekoppelteDrucker.filter(p => p.aktiv);
    }

    async druckeHtml(html: string, drucker: DruckerKonfiguration): Promise<boolean> {
        log?.debug('druckeHtml');

        if (!MyApp.isCordovaAvailable()) {
            log?.info(html);
            return true;
        }

        const options: PrintOptions = {
            name: 'MyDocument',
            // printerId: 'printer007',
            duplex: false,
        };

        try {
            this.druckerStatus.next(GeraetStatusTyp.WirdGedruckt);

            const printResult = await this.printer.print(html, options);

            // FIXME: Das Ergebnis stimmt hier leider nicht. Mal wird true, mal false zurückgegeben. Unabhängig davon ob der Druck wirklich ausgeführt wurde
            log?.info('printResult: ' + printResult);

            if (printResult || true) {
                this.druckerStatus.next(GeraetStatusTyp.Verbunden);
                return true;
            } else {
                return false;
            }
        } catch (err) {
            log.warn('druckeHtml: ' + Utils.getErrorMessage(err), err);
            this.druckerStatus.next(GeraetStatusTyp.Fehler);
            return false;
        }
    }

    async druckeZpl(zpl: string, drucker: DruckerKonfiguration): Promise<boolean> {
        const appConfig = AppConfig.current;

        if (!MyApp.isCordovaAvailable()) {
            log?.info(zpl);
            return true;
        }

        await UiHelper.showLoading("Warte auf Drucker...");

        if (this.sucheDruckerPromise) {
            // Warte bis Suche abgeschlossen ist
            await this.sucheDruckerPromise;
        }

        if (this.druckenPromise) {
            log?.info('Warte bis Suche abgeschlossen ist...');
            await this.druckenPromise;
        }

        if (this.zebraStatusLaeuft) {
            log?.info('Warte bis Statusprüfung abgeschlossen ist...');
            await this.druckenPromise;
        }

        const zbtprinter = (cordova.plugins as any).zbtprinter as ZbtPrinter;

        if (!zbtprinter) {
            UiHelper.showAlert("ZbtPrinter Plugin nicht installiert");
            return false;
        }

        await UiHelper.showLoading("Wird gedruckt...");

        this.druckenPromise = new Promise((resolve, reject) => {
            let callbackErhalten = false;
            let zeitueberschreitung = false;

            zbtprinter.print(drucker.macAdresse, zpl,
                success => {
                    this.druckenPromise = null;
                    callbackErhalten = true;
                    this.letzteSuche = Date.now();

                    if (!zeitueberschreitung) {
                        log?.info("Druck erfolgreich");

                        this.ngZone.run(() => {
                            UiHelper.hideLoading();
                            this.druckerStatus.next(GeraetStatusTyp.Verbunden);
                            resolve(true);
                        });
                    } else {
                        log.warn("Druck erfolgreich (nach Timeout!)");
                    }
                }, fail => {
                    this.druckenPromise = null;
                    callbackErhalten = true;

                    const msg = Utils.getErrorMessage(fail);

                    if (msg.startsWith('Could not connect to device: read failed, socket might closed or timeout, read ret: -1')) {
                        // Bluetooth-Problem.
                        // Lässt sich dur einen Neustart der App beheben. Das muss aber eigentlich auch anders gehen
                        // TODO zbtprinter.
                    }

                    log.warn("Druck fehlgeschlagen: " + Utils.getErrorMessage(fail), fail);

                    if (!zeitueberschreitung) {
                        this.ngZone.run(() => {
                            UiHelper.hideLoading();
                            UiHelper.showError(fail);
                            this.druckerStatus.next(GeraetStatusTyp.Fehler);
                            resolve(false);
                        });
                    }
                }
            );

            setTimeout(() => {
                if (!callbackErhalten) {
                    log.warn('Druck Timeout');
                    zeitueberschreitung = true;
                    this.druckenPromise = null;

                    UiHelper.hideLoading();
                    UiHelper.showAlert('Zeitüberschreitung beim Drucken');
                    this.druckerStatus.next(GeraetStatusTyp.Fehler);

                    resolve(false);
                }
            }, MyApp.current.druckenTimeout);
        });

        return this.druckenPromise;
    }

    async druckeEscPos(buffer: Uint8Array, drucker: DruckerKonfiguration): Promise<boolean> {
        const appConfig = AppConfig.current;

        if (!MyApp.isCordovaAvailable()) {
            return true;
        }

        try {
            await UiHelper.showLoading("Warte auf Drucker...");

            // 1. Verbindung herstellen
            const connectError = await this.connectBluetooth(drucker.macAdresse);

            if (connectError) {
                UiHelper.showError(connectError);
                return false;
            }

            await UiHelper.showLoading("Wird gedruckt...");

            log.debug('Drucke ESC/Pos Buffer size: ' + buffer.length);

            // 2. Daten senden (in 20-Byte-Paketen für maximale Stabilität)
            let chunkSize = Utils.parseInt(AppConfig.current.einstellungen?.ESCPosBufferSize);
            let chunkDelayTime = Utils.parseInt(AppConfig.current.einstellungen?.ESCPosBufferDelayTime);

            if (chunkSize <= 0) {
                chunkSize = 20;
            }

            for (let i = 0; i < buffer.length; i += chunkSize) {
                const chunk = buffer.slice(i, i + chunkSize);

                await this.bluetoothSerial.write(chunk.buffer);

                if (chunkDelayTime > 0) {
                    await Utils.delay(chunkDelayTime);
                }
            }

            await UiHelper.showLoading("Druck abgeschlossen!");

            // 3. Optional: Kurz warten und Verbindung trennen
            await Utils.delay(500);

            this.bluetoothSerial.disconnect();

            return true;
        } catch (err) {
            UiHelper.showError(err);

            return false;
        } finally {
            if (this.connectSubscription) {
                this.connectSubscription.unsubscribe();
                this.connectSubscription = null;
            }

            UiHelper.hideLoading();
        }
    }

    connectBluetooth(macAdresse: string): Promise<string> {
        let isResolved = false;

        return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                if (!isResolved) {
                    log.warn('connectBluetooth: Timeout');
                    isResolved = true;
                    resolve('Timeout');
                }
            }, 15000);

            try {
                this.connectSubscription = this.bluetoothSerial.connect(macAdresse).subscribe({
                    next: (value) => {
                        log?.debug('Connect Next: ' + value);
                        isResolved = true;
                        clearTimeout(timeoutId);
                        resolve('');
                    },
                    error: (err) => {
                        let errMessage = Utils.getErrorMessage(err);

                        log.warn('Connect Error: ' + errMessage);
                        isResolved = true;
                        clearTimeout(timeoutId);
                        resolve(errMessage);
                    },
                    complete: () => {
                        log?.debug('Connect Complete');
                    }
                });
            } catch (err) {
                log.warn('getZebraPrinterName: ' + Utils.getErrorMessage(macAdresse));
                isResolved = true;
                clearTimeout(timeoutId);
                resolve(Utils.getErrorMessage(err));
            }
        });

    }

    koppeln(device: DruckerDevice) {
        const appConfig = AppConfig.current;

        if (!device.name) {
            device.name = device.address;
        }

        appConfig.gekoppelteDrucker.push({
            typ: 'ZEBRA',
            aktiv: true,
            macAdresse: device.address,
            name: device.name,
            zuletztVerwendet: new Date().toISOString(),
            istVorkonfiguriert: false
        });

        this.systemService.speichereAppConfig(appConfig);

        this.druckerStatus.next(GeraetStatusTyp.Verbunden);
    }

    async testDruck(druckerKonfiguration: DruckerKonfiguration) {
        log?.debug('testDruck: ' + druckerKonfiguration.name);

        try {
            switch (AppConfig.current.einstellungen.DruckerTyp) {
                case 'Druckdienst':
                    await this.testDruckStandard(druckerKonfiguration);
                    break;

                case 'zebra':
                    await this.testDruckZebra(druckerKonfiguration);
                    break;

                case 'escpos':
                    await this.testDruckEscPos(druckerKonfiguration);
                    break;

                default:
                    throw new Error('unbekannter DruckerTyp: ' + AppConfig.current.einstellungen.DruckerTyp);
            }
        } catch (err) {
            log.error('onDruckerTest', err);
            UiHelper.hideLoading();
            UiHelper.showError(err);
        }
    }

    private async testDruckZebra(druckerKonfiguration: DruckerKonfiguration): Promise<void> {
        log?.debug('testDruckZebra: ' + druckerKonfiguration.name);

        let zpl = AppConfig.current.einstellungen.DruckerTestCode;

        if (!zpl) {
            zpl = "^XA"
                + "^FO20,20^A0N,25,25^FDThis is a ZPL test.^FS"
                + "^XZ";
        }

        await this.druckeZpl(zpl, druckerKonfiguration);
    }

    private async testDruckEscPos(cfg: DruckerKonfiguration): Promise<void> {
        log?.debug('testDruckEscPos: ' + cfg.name);

        const opts: any = {
            // printerModel: 'epson-tm-t88vi',
            language: 'esc-pos',
            // feedBeforeCut: 4,
        };

        if (cfg.model) {
            opts.printerModel = cfg.model;
        }

        if (cfg.width) {
            opts.columns = Utils.parseInt(cfg.width);
        }

        const encoder = new ReceiptPrinterEncoder(opts);

        const ruler = '123456789012345678901234567890123456789012345678901234567890';

        // siehe: https://github.com/NielsLeenheer/ReceiptPrinterEncoder/blob/main/documentation/commands.md
        let buffer = encoder
            .initialize()
            // .codepage('cp437')
            .text('Test Font A (Standard):')
            .newline()
            .font('A')
            .text(ruler)
            .newline()
            .line('--------------------------')
            .newline()

            .text('Test Font B (Small):')
            .newline()
            .font('B')
            .text(ruler)
            .newline()
            .line('--------------------------')
            .newline()

            // .line('Test Size 1:')
            // .size(1, 1)
            // .line('Das ist ein Test')
            // .line('--------------------------')

            // .line('Test Size 2:')
            // .size(2, 2)
            // .line('Das ist ein Test')
            // .line('--------------------------')

            // .line('Test Size 3:')
            // .size(3, 3)
            // .line('Das ist ein Test')
            // .line('--------------------------')

            .line('Print mode 0:')
            .raw([0x1b, 0x21, 0x00])
            .line('Das ist ein Test')
            .line('--------------------------')

            .line('Print mode 1:')
            .raw([0x1b, 0x21, 0x01])
            .line('Das ist ein Test')
            .line('--------------------------')

            .text('Test Rules')
            .size(1, 1)
            .newline()
            .rule({ style: 'double' })
            .newline()
            .rule({ style: 'single' })
            .newline()

            .cut()
            .encode();

        await this.druckeEscPos(buffer, cfg);
    }

    private async testDruckStandard(druckerKonfiguration: DruckerKonfiguration) {
        log?.debug('testDruckStandard: ' + druckerKonfiguration.name);

        const options: PrintOptions = {
            name: 'MyDocument',
            // printerId: 'printer007',
            duplex: true,
        };

        const content = "Es flattert um die Quelle\nDie wechselnde Libelle,...";

        const printResult = await this.printer.print(content, options);

        log?.debug('printResult: ' + printResult, printResult);
    }
}
